import { 
  Connection, 
  LAMPORTS_PER_SOL, 
  PublicKey, 
  SystemProgram, 
  Transaction,
  sendAndConfirmTransaction,
  Keypair
} from '@solana/web3.js';
import { AnchorProvider } from '@project-serum/anchor';
import bs58 from 'bs58';

// Use IndexedDB for cross-browser persistence
class AirdropStorage {
  private static DB_NAME = 'FaucetDatabase';
  private static STORE_NAME = 'airdropRecords';
  private static VERSION = 1;

  // Open IndexedDB connection
  static async openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.VERSION);
      
      request.onerror = (event) => {
        reject('Error opening database');
      };
      
      request.onsuccess = (event) => {
        resolve(request.result);
      };
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'address' });
        }
      };
    });
  }

  // Get airdrop timestamp for a wallet address
  static async getAirdropTime(address: string): Promise<number | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.STORE_NAME, 'readonly');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(address);
        
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result.timestamp);
          } else {
            resolve(null);
          }
        };
        
        request.onerror = () => {
          reject('Error reading from database');
        };
      });
    } catch (error) {
      console.error('IndexedDB error:', error);
      return null;
    }
  }

  // Set airdrop timestamp for a wallet address
  static async setAirdropTime(address: string, timestamp: number): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.STORE_NAME, 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.put({ address, timestamp });
        
        request.onsuccess = () => {
          resolve();
        };
        
        request.onerror = () => {
          reject('Error writing to database');
        };
      });
    } catch (error) {
      console.error('IndexedDB error:', error);
    }
  }
}

// Fallback to localStorage if IndexedDB fails
const lastAirdropTimes: Record<string, number> = {};

const loadLastAirdropTimes = () => {
  const saved = localStorage.getItem('lastAirdropTimes');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      Object.assign(lastAirdropTimes, parsed);
    } catch (e) {
      console.error('Failed to load airdrop times from localStorage', e);
    }
  }
};

const saveLastAirdropTimes = () => {
  localStorage.setItem('lastAirdropTimes', JSON.stringify(lastAirdropTimes));
};

// Load times from localStorage on init
loadLastAirdropTimes();

// Check if a wallet is eligible for airdrop
export async function isEligibleForAirdrop(walletAddress: string): Promise<boolean> {
  try {
    // First try to get from IndexedDB
    const lastAirdropTime = await AirdropStorage.getAirdropTime(walletAddress);
    
    if (lastAirdropTime) {
      const now = Date.now();
      const hoursPassed = (now - lastAirdropTime) / (1000 * 60 * 60);
      return hoursPassed >= 24;
    }
    
    // Fallback to localStorage if IndexedDB had no record
    const localLastAirdrop = lastAirdropTimes[walletAddress];
    if (localLastAirdrop) {
      const now = Date.now();
      const hoursPassed = (now - localLastAirdrop) / (1000 * 60 * 60);
      return hoursPassed >= 24;
    }
    
    // No records found, eligible for airdrop
    return true;
  } catch (error) {
    console.error('Error checking eligibility:', error);
    
    // Last resort fallback to localStorage
    const lastAirdrop = lastAirdropTimes[walletAddress];
    if (lastAirdrop) {
      const now = Date.now();
      const hoursPassed = (now - lastAirdrop) / (1000 * 60 * 60);
      return hoursPassed >= 24;
    }
    return true;
  }
}

// Get time remaining until next eligible airdrop
export async function getTimeUntilNextAirdrop(walletAddress: string): Promise<number> {
  try {
    // First try to get from IndexedDB
    const lastAirdropTime = await AirdropStorage.getAirdropTime(walletAddress);
    
    if (lastAirdropTime) {
      const now = Date.now();
      const nextEligibleTime = lastAirdropTime + 24 * 60 * 60 * 1000;
      const timeRemaining = nextEligibleTime - now;
      return timeRemaining > 0 ? timeRemaining : 0;
    }
    
    // Fallback to localStorage
    const localLastAirdrop = lastAirdropTimes[walletAddress];
    if (localLastAirdrop) {
      const now = Date.now();
      const nextEligibleTime = localLastAirdrop + 24 * 60 * 60 * 1000;
      const timeRemaining = nextEligibleTime - now;
      return timeRemaining > 0 ? timeRemaining : 0;
    }
    
    return 0;
  } catch (error) {
    console.error('Error getting time until next airdrop:', error);
    
    // Last resort fallback
    const lastAirdrop = lastAirdropTimes[walletAddress];
    if (lastAirdrop) {
      const now = Date.now();
      const nextEligibleTime = lastAirdrop + 24 * 60 * 60 * 1000;
      const timeRemaining = nextEligibleTime - now;
      return timeRemaining > 0 ? timeRemaining : 0;
    }
    return 0;
  }
}

// Format time remaining to readable format
export function formatTimeRemaining(milliseconds: number): string {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${hours}h ${minutes}m`;
}

// Record a failed attempt
export async function recordFailedAttempt(walletAddress: string): Promise<void> {
  try {
    const now = Date.now();
    await AirdropStorage.setAirdropTime(walletAddress, now);
    lastAirdropTimes[walletAddress] = now;
    saveLastAirdropTimes();
    console.log('Failed attempt recorded for', walletAddress);
  } catch (error) {
    console.error('Error recording failed attempt:', error);
  }
}

// Send 2 SOL from the faucet wallet to the connected wallet
export async function requestDevnetSol(
  connection: Connection,
  recipientWalletAddress: string
): Promise<string> {
  try {
    // Check eligibility from our persistent storage
    const isEligible = await isEligibleForAirdrop(recipientWalletAddress);
    
    if (!isEligible) {
      const timeRemaining = await getTimeUntilNextAirdrop(recipientWalletAddress);
      throw new Error(`Airdrop not available. Try again in ${formatTimeRemaining(timeRemaining)}`);
    }

    // Using connection.requestAirdrop for devnet
    const signature = await connection.requestAirdrop(
      new PublicKey(recipientWalletAddress),
      2 * LAMPORTS_PER_SOL
    );

    // Wait for confirmation
    await connection.confirmTransaction(signature, 'confirmed');
    
    // Update records in both storage systems
    const now = Date.now();
    await AirdropStorage.setAirdropTime(recipientWalletAddress, now);
    lastAirdropTimes[recipientWalletAddress] = now;
    saveLastAirdropTimes();
    
    return signature;
  } catch (error: any) {
    console.error('Error sending SOL:', error);
    
    // Check if this is an airdrop limit error (HTTP 429)
    if (error.message && (
      error.message.includes("airdrop limit") || 
      error.message.includes("429") ||
      error.message.includes("run dry")
    )) {
      // Record the failed attempt in storage to prevent immediate retry
      await recordFailedAttempt(recipientWalletAddress);
      throw new Error("You've either reached your airdrop limit today. Please visit https://solfaucet.com for alternate sources of test SOL.");
    }
    
    throw new Error(error.message || 'Failed to send SOL');
  }
} 